import {useContext, useEffect, useRef, useState} from "react";
import axios from "axios";
import {v4 as uuidv4} from "uuid";

// Context
import {UserAuthenticationContext} from "../../../Context/UserAuthenticationContext";

// API
import {
    AcceptRetentionOffer,
    CancellationOptions,
    CancelStripeSubscription,
    GetCancellationOptions,
    PauseSubscription,
} from "../../../Api/Stripe";

// Models
import {AuthUser} from "../../../Models/DTOs/AuthUser";
import {SubscriptionDTO} from "../../../Models/DTOs/Subscription";

// Helpers
import {formatMoney} from "../../../Helpers/Currency/Currency";
import {toGBDate} from "../../../Helpers/DataTypesGeneral/DateAndTime";
import {normalizePauseError} from "../../../Helpers/Subscription";

// Components
import ButtonPill from "../../Buttons/ButtonPill";
import IconSpinLoader from "../../../Assets/SVGs/Icons/Loading/SpinLoader";

export type CancellationStage =
    | "loading"
    | "discount"
    | "pause"
    | "reason"
    | "submitting"
    | "success"
    | "retained";

type SubmissionKind = "discount" | "pause" | "cancel";

type CancelOrPauseProps = {
    userData: AuthUser | null;
    sub: SubscriptionDTO | null;
};

const SUPPORT_EMAIL = "membership@ickonic.com";

function hasUsableDiscount(options: CancellationOptions): boolean {
    const hasFinalPrice = options.discount.final_amount_minor != null &&
        options.discount.final_amount_minor >= 0;
    const hasLegacyDiscount = options.discount.amount_off_minor != null &&
        options.discount.amount_off_minor > 0;
    if (options.discount.pricing_mode === "prepaid") {
        return !!(
            options.discount.eligible && options.discount.offer_key && options.discount.currency &&
            hasFinalPrice && options.discount.final_amount_minor! > 0 &&
            options.discount.base_amount_minor != null && options.discount.base_amount_minor > 0 &&
            prepaidRetentionPeriod(options) &&
            (options.cadence === "month" || options.cadence === "year")
        );
    }

    return !!(
        options.discount?.eligible &&
        options.discount.offer_key &&
        options.discount.currency &&
        (hasFinalPrice || hasLegacyDiscount)
    );
}

function hasUsablePause(options: CancellationOptions): boolean {
    return !!(
        options.pause?.eligible &&
        Array.isArray(options.pause.allowed_duration_months) &&
        options.pause.allowed_duration_months.length > 0
    );
}

export function firstCancellationStage(options: CancellationOptions): CancellationStage {
    if (hasUsableDiscount(options)) return "discount";
    if (hasUsablePause(options)) return "pause";
    return "reason";
}

export function stageAfterDiscount(options: CancellationOptions): CancellationStage {
    return hasUsablePause(options) ? "pause" : "reason";
}

function prepaidRetentionPeriod(options: CancellationOptions): string | null {
    const {intro_interval: unit, intro_interval_count: count} = options.discount;
    if ((unit !== "month" && unit !== "year") || count == null || !Number.isInteger(count) || count <= 0) {
        return null;
    }
    return count + " " + unit + (count === 1 ? "" : "s");
}

export function prepaidRetentionTerms(options: CancellationOptions, billingInterval?: string | null): string | null {
    if (options.discount.pricing_mode !== "prepaid") return null;
    const startsAt = toGBDate(options.discount.starts_at_utc);
    const starts = startsAt ? "at your next renewal on " + startsAt : "at your next renewal";
    const regularPrice = formatMoney(options.discount.base_amount_minor, options.discount.currency);
    const cadence = options.cadence ?? billingInterval;
    const renewal = regularPrice && (cadence === "month" || cadence === "year")
        ? regularPrice + " per " + cadence
        : "your regular price";
    return "No charge today. You keep the time you have already paid for. Your single payment is due " + starts +
        ", when the offer starts. After the offer period, your membership renews at " + renewal + ".";
}

export function discountTerms(options: CancellationOptions, billingInterval?: string | null): string {
    const discount = options.discount;
    if (discount.pricing_mode === "prepaid") {
        const finalPrice = formatMoney(discount.final_amount_minor, discount.currency) ?? "the offered price";
        return "Pay " + finalPrice + " once for " + (prepaidRetentionPeriod(options) ?? "the offer period");
    }
    if (discount.final_amount_minor != null && discount.final_amount_minor >= 0) {
        const finalPrice = formatMoney(discount.final_amount_minor, discount.currency) ?? "the offered price";
        if (discount.coupon_duration === "repeating" && discount.coupon_duration_months) {
            return `Pay ${finalPrice} per month for ${discount.coupon_duration_months} ${discount.coupon_duration_months === 1 ? "month" : "months"}`;
        }
        if (discount.coupon_duration === "once" && billingInterval === "year") {
            return `Pay ${finalPrice} for your next annual membership period`;
        }
        return `Keep your membership for ${finalPrice}`;
    }

    // Compatibility for an older API during a staggered deployment. This text
    // describes the saving only; it never presents amount_off as the price due.
    const amount = formatMoney(discount.amount_off_minor, discount.currency) ?? "the offered amount";

    switch (discount.coupon_duration) {
        case "once":
            return billingInterval === "year"
                ? `Save ${amount} on your next annual renewal`
                : `Save ${amount} on your next membership payment`;
        case "repeating":
            if (discount.coupon_duration_months && billingInterval === "month") {
                return `Save ${amount} on each of your next ${discount.coupon_duration_months} monthly payments`;
            }
            return discount.coupon_duration_months
                ? `Save ${amount} on eligible membership charges for ${discount.coupon_duration_months} months`
                : `Save ${amount} on eligible membership charges for the offer period`;
        case "forever":
            return billingInterval === "month"
                ? `Save ${amount} on each future monthly payment`
                : `Save ${amount} on future eligible membership charges`;
        default:
            return `Save ${amount} on your membership`;
    }
}

function SupportFallback({message}: {message: string}) {
    return (
        <div className="cancel-or-pause__support-fallback" role="alert">
            <p>
                {message}{" "}
                Please email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we will help you.
            </p>
        </div>
    );
}

const CancelOrPause = ({userData, sub}: CancelOrPauseProps) => {
    const authCtx = useContext(UserAuthenticationContext);
    const userId = userData?.id ?? sub?.UserId ?? authCtx.userData?.id ?? "";
    const requestIdRef = useRef(uuidv4());
    const activeControllerRef = useRef<AbortController | null>(null);
    const isSubmittingRef = useRef(false);

    const [stage, setStage] = useState<CancellationStage>("loading");
    const [options, setOptions] = useState<CancellationOptions | null>(null);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [loadError, setLoadError] = useState("");
    const [actionError, setActionError] = useState("");
    const [submissionKind, setSubmissionKind] = useState<SubmissionKind | null>(null);
    const [selectedPauseMonths, setSelectedPauseMonths] = useState<number | null>(null);
    const [selectedReason, setSelectedReason] = useState("");
    const [comment, setComment] = useState("");
    const [reasonError, setReasonError] = useState("");
    const [retainedMessage, setRetainedMessage] = useState("");
    const [cancelEffectiveAt, setCancelEffectiveAt] = useState<string | null>(null);

    const newController = () => {
        activeControllerRef.current?.abort();
        const controller = new AbortController();
        activeControllerRef.current = controller;
        return controller;
    };

    const refreshAccount = () => window.location.reload();

    useEffect(() => {
        let isCurrent = true;
        const controller = newController();

        const loadOptions = async () => {
            setStage("loading");
            setLoadError("");
            setActionError("");

            if (!userId || sub?.ProviderId !== 1) {
                setLoadError("Online cancellation is not available for this membership.");
                return;
            }

            let accessToken: string | null = null;
            try {
                accessToken = await authCtx.getValidAccessToken();
            } catch {
                if (isCurrent) {
                    setLoadError("We could not verify your session. Please sign in again, or contact us to cancel.");
                }
                return;
            }
            if (!isCurrent || controller.signal.aborted) return;
            if (!accessToken) {
                setLoadError("Your session has expired. Please sign in again, or contact us to cancel.");
                return;
            }

            const response = await GetCancellationOptions(userId, accessToken, controller);
            if (!isCurrent || controller.signal.aborted) return;

            if (axios.isAxiosError(response)) {
                setLoadError("We could not load your cancellation options right now.");
                return;
            }

            const reasons = Array.isArray(response.cancellation?.reasons)
                ? response.cancellation.reasons.filter((reason) => reason.code?.trim() && reason.label?.trim())
                : [];

            if (reasons.length === 0) {
                setLoadError("We could not load the cancellation reasons right now.");
                return;
            }

            const normalizedOptions: CancellationOptions = {
                ...response,
                pause: {
                    ...response.pause,
                    allowed_duration_months: Array.isArray(response.pause?.allowed_duration_months)
                        ? response.pause.allowed_duration_months.filter((months) => Number.isInteger(months) && months > 0)
                        : [],
                },
                cancellation: {...response.cancellation, reasons},
            };

            setOptions(normalizedOptions);
            setSelectedPauseMonths(null);
            setSelectedReason("");
            setComment("");
            setStage(firstCancellationStage(normalizedOptions));
        };

        void loadOptions();

        return () => {
            isCurrent = false;
            controller.abort();
        };

        // getValidAccessToken is supplied by the authentication provider and is
        // intentionally not a dependency: reopening the popup remounts this flow.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, sub?.ProviderId, loadAttempt]);

    const failSubmission = (returnStage: CancellationStage, message: string) => {
        isSubmittingRef.current = false;
        setActionError(message);
        setSubmissionKind(null);
        setStage(returnStage);
    };

    const getActionToken = async (returnStage: CancellationStage): Promise<string | null> => {
        let accessToken: string | null = null;
        try {
            accessToken = await authCtx.getValidAccessToken();
        } catch {
            failSubmission(returnStage, "We could not verify your session. Please sign in again, or contact us for help.");
            return null;
        }
        if (!accessToken) {
            failSubmission(returnStage, "Your session has expired. Please sign in again, or contact us for help.");
            return null;
        }
        return accessToken;
    };

    const handleAcceptDiscount = async () => {
        const offerKey = options?.discount.offer_key;
        if (!options || !offerKey || !userId || isSubmittingRef.current) return;

        isSubmittingRef.current = true;
        setActionError("");
        setSubmissionKind("discount");
        setStage("submitting");

        const accessToken = await getActionToken("discount");
        if (!accessToken) return;

        const controller = newController();
        const response = await AcceptRetentionOffer(
            userId,
            offerKey,
            requestIdRef.current,
            accessToken,
            controller,
            options.discount
        );

        if (axios.isAxiosError(response) || response.accepted !== true) {
            failSubmission("discount", "We could not apply this offer. You can retry or continue with cancellation.");
            return;
        }

        // The accepted response owns the actual start date, including a renewal
        // that occurred while the member was considering the offer.
        const acceptedOptions = {
            ...options,
            discount: {...options.discount, ...response.offer},
        };
        const offerTerms = prepaidRetentionTerms(acceptedOptions, options.cadence ?? sub?.BillingInterval);
        setRetainedMessage(offerTerms
            ? "Your offer is scheduled. " + discountTerms(acceptedOptions) + ". " + offerTerms
            : "Your offer has been applied. " + discountTerms(acceptedOptions, options.cadence ?? sub?.BillingInterval) + ".");
        isSubmittingRef.current = false;
        setSubmissionKind(null);
        setStage("retained");
    };

    const handlePause = async () => {
        if (!options || !userId || selectedPauseMonths == null || isSubmittingRef.current) return;

        isSubmittingRef.current = true;
        setActionError("");
        setSubmissionKind("pause");
        setStage("submitting");

        const accessToken = await getActionToken("pause");
        if (!accessToken) return;

        const controller = newController();
        const response = await PauseSubscription(
            userId,
            selectedPauseMonths,
            accessToken,
            controller
        );

        if (axios.isAxiosError(response)) {
            failSubmission("pause", normalizePauseError(response));
            return;
        }

        if (!response.paused) {
            failSubmission("pause", "We could not pause your membership. You can retry or continue with cancellation.");
            return;
        }

        const accessUntil = toGBDate(response.access_until) ?? "the end of your current paid period";
        const resumesAt = toGBDate(response.resumes_at) ?? "the scheduled date";
        setRetainedMessage(
            `Your membership is paused. You keep access until ${accessUntil}, and it will automatically resume on ${resumesAt}.`
        );
        isSubmittingRef.current = false;
        setSubmissionKind(null);
        setStage("retained");
    };

    const handleCancelMembership = async () => {
        if (!options || !userId || isSubmittingRef.current) return;
        if (!selectedReason) {
            setReasonError("Please select a reason before cancelling.");
            return;
        }

        isSubmittingRef.current = true;
        setActionError("");
        setReasonError("");
        setSubmissionKind("cancel");
        setStage("submitting");

        const accessToken = await getActionToken("reason");
        if (!accessToken) return;

        const controller = newController();
        const response = await CancelStripeSubscription(
            userId,
            false,
            accessToken,
            controller,
            {
                reasonCode: selectedReason,
                comment: comment.trim(),
                requestId: requestIdRef.current,
            }
        );

        if (axios.isAxiosError(response) || response.cancelled !== true) {
            failSubmission(
                "reason",
                "We could not schedule your cancellation. Please retry."
            );
            return;
        }

        setCancelEffectiveAt(response.cancels_at_utc ?? options.cancellation.effective_at_utc);
        isSubmittingRef.current = false;
        setSubmissionKind(null);
        setStage("success");
    };

    const submittingText =
        submissionKind === "discount"
            ? "Applying your offer…"
            : submissionKind === "pause"
                ? "Pausing your membership…"
                : "Scheduling your cancellation…";

    return (
        <form className="form--cancel-or-pause is-dark-mode" onSubmit={(event) => event.preventDefault()}>
            {stage === "loading" && (
                <>
                    <div className="cancel-or-pause__heading-wrapper">
                        <h1>Manage Your Membership</h1>
                    </div>
                    <div className="cancel-or-pause__divider"/>
                    <div className="cancel-or-pause__content-wrapper" aria-live="polite">
                        {loadError ? (
                            <>
                                <SupportFallback message={loadError}/>
                                {userId && sub?.ProviderId === 1 && (
                                    <ButtonPill
                                        label="Try Again"
                                        onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                                    />
                                )}
                            </>
                        ) : (
                            <div className="cancel-or-pause__loading">
                                {IconSpinLoader({className: "icon--loader"})}
                                <p>Reviewing the options available for your membership…</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {stage === "discount" && options && (
                <>
                    <div className="cancel-or-pause__heading-wrapper">
                        <h1>{options.discount.badge?.trim() || "Before You Go"}</h1>
                    </div>
                    <div className="cancel-or-pause__divider"/>
                    <div className="cancel-or-pause__content-wrapper">
                        <div className="cancel-or-pause__offer-card">
                            <h2>{options.discount.headline?.trim() || "We would love you to stay"}</h2>
                            <p className="cancel-or-pause__offer-value">
                                {discountTerms(options, options.cadence ?? sub?.BillingInterval)}
                            </p>
                            {options.discount.pricing_mode === "prepaid" && (
                                <p className="cancel-or-pause__offer-terms">
                                    {prepaidRetentionTerms(options, options.cadence ?? sub?.BillingInterval)}
                                </p>
                            )}
                        </div>
                        <p>
                            {options.discount.description?.trim() ||
                                "Accept this limited offer and keep your membership active. The offer can only be claimed when the account is eligible."}
                        </p>
                        {options.discount.terms?.trim() && (
                            <p className="cancel-or-pause__offer-terms">{options.discount.terms.trim()}</p>
                        )}
                        {actionError && <SupportFallback message={actionError}/>}
                        <div className="cancel-or-pause__actions">
                            <ButtonPill
                                label={options.discount.cta_label?.trim() || "Apply Offer and Stay"}
                                className="cancel-or-pause__primary"
                                onClick={handleAcceptDiscount}
                            />
                            <ButtonPill
                                label="No Thanks, Continue"
                                className="cancel-or-pause__secondary"
                                onClick={() => {
                                    setActionError("");
                                    setStage(stageAfterDiscount(options));
                                }}
                            />
                        </div>
                    </div>
                </>
            )}

            {stage === "pause" && options && (
                <>
                    <div className="cancel-or-pause__heading-wrapper">
                        <h1>Would a Break Help?</h1>
                    </div>
                    <div className="cancel-or-pause__divider"/>
                    <div className="cancel-or-pause__content-wrapper">
                        <p>
                            Pause billing instead of cancelling. You will keep access through the period you have
                            already paid for, then your membership will automatically resume after your chosen break.
                        </p>
                        <fieldset className="cancel-or-pause__choice-list cancel-or-pause__choice-list--durations">
                            <legend>Choose a pause duration</legend>
                            {options.pause.allowed_duration_months.map((months) => (
                                <label key={months} className={selectedPauseMonths === months ? "is-selected" : ""}>
                                    <input
                                        type="radio"
                                        name="pause-duration"
                                        value={months}
                                        checked={selectedPauseMonths === months}
                                        onChange={() => setSelectedPauseMonths(months)}
                                    />
                                    <span>{months} {months === 1 ? "month" : "months"}</span>
                                </label>
                            ))}
                        </fieldset>
                        {actionError && <SupportFallback message={actionError}/>}
                        <div className="cancel-or-pause__actions">
                            <ButtonPill
                                label="Pause Membership"
                                className={`cancel-or-pause__primary ${selectedPauseMonths == null ? "is-not-valid" : ""}`}
                                disabled={selectedPauseMonths == null}
                                onClick={handlePause}
                            />
                            <ButtonPill
                                label="No Thanks, Continue to Cancellation"
                                className="cancel-or-pause__secondary"
                                onClick={() => {
                                    setActionError("");
                                    setStage("reason");
                                }}
                            />
                        </div>
                    </div>
                </>
            )}

            {stage === "reason" && options && (
                <>
                    <div className="cancel-or-pause__heading-wrapper">
                        <h1>Why Are You Leaving?</h1>
                    </div>
                    <div className="cancel-or-pause__divider"/>
                    <div className="cancel-or-pause__content-wrapper">
                        <p>Your answer helps us improve Ickonic and communicate more relevant updates in the future.</p>
                        <fieldset className="cancel-or-pause__choice-list">
                            <legend>Select one reason</legend>
                            {options.cancellation.reasons.map((reason) => (
                                <label key={reason.code} className={selectedReason === reason.code ? "is-selected" : ""}>
                                    <input
                                        type="radio"
                                        name="cancellation-reason"
                                        value={reason.code}
                                        checked={selectedReason === reason.code}
                                        onChange={() => {
                                            setSelectedReason(reason.code);
                                            setReasonError("");
                                        }}
                                    />
                                    <span>{reason.label}</span>
                                </label>
                            ))}
                        </fieldset>
                        {reasonError && <p className="cancel-or-pause__error" role="alert">{reasonError}</p>}
                        <div className="cancel-or-pause__comment">
                            <label htmlFor="cancellation-comment">Anything else you would like us to know? (optional)</label>
                            <textarea
                                id="cancellation-comment"
                                name="cancellation-comment"
                                value={comment}
                                maxLength={1000}
                                onChange={(event) => setComment(event.target.value)}
                            />
                        </div>
                        {options.cancellation.effective_at_utc && (
                            <p className="cancel-or-pause__effective-date">
                                Cancelling now stops auto-renewal. Your access is scheduled to continue until{" "}
                                <strong>{toGBDate(options.cancellation.effective_at_utc)}</strong>.
                            </p>
                        )}
                        {actionError && <SupportFallback message={actionError}/>}
                        <div className="cancel-or-pause__actions">
                            <ButtonPill
                                label="Cancel at End of Billing Period"
                                className={`cancel-or-pause__danger ${!selectedReason ? "is-not-valid" : ""}`}
                                disabled={!selectedReason}
                                onClick={handleCancelMembership}
                            />
                        </div>
                    </div>
                </>
            )}

            {stage === "submitting" && (
                <>
                    <div className="cancel-or-pause__heading-wrapper">
                        <h1>Please Wait</h1>
                    </div>
                    <div className="cancel-or-pause__divider"/>
                    <div className="cancel-or-pause__content-wrapper" aria-live="polite">
                        <div className="cancel-or-pause__loading">
                            {IconSpinLoader({className: "icon--loader"})}
                            <p>{submittingText}</p>
                        </div>
                    </div>
                </>
            )}

            {stage === "retained" && (
                <>
                    <div className="cancel-or-pause__heading-wrapper">
                        <h1>Your Membership Has Been Updated</h1>
                    </div>
                    <div className="cancel-or-pause__divider"/>
                    <div className="cancel-or-pause__content-wrapper" aria-live="polite">
                        <p className="cancel-or-pause__success">{retainedMessage}</p>
                        <ButtonPill label="Return to My Account" onClick={refreshAccount}/>
                    </div>
                </>
            )}

            {stage === "success" && options && (
                <>
                    <div className="cancel-or-pause__heading-wrapper">
                        <h1>Cancellation Scheduled</h1>
                    </div>
                    <div className="cancel-or-pause__divider"/>
                    <div className="cancel-or-pause__content-wrapper" aria-live="polite">
                        <p className="cancel-or-pause__success">
                            Your membership will not renew
                            {cancelEffectiveAt
                                ? ` and your access will continue until ${toGBDate(cancelEffectiveAt)}`
                                : " at the end of the current billing period"}.
                        </p>
                        <ButtonPill label="Return to My Account" onClick={refreshAccount}/>
                    </div>
                </>
            )}
        </form>
    );
};

export default CancelOrPause;
